import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import styled, { css, ThemeContext } from 'styled-components';
import { shallow } from 'zustand/shallow';

import Menu from './Menu';
import { LogoDiv, Divider } from '../../../styles/LayoutStyles';
import SelectLanguage from '../SelectLanguage';
import UserInfo from './UserInfo';
import NewButton from '../../common/button/NewButton';

import { LayoutContext } from '../../../context/LayoutProvider';
import { ModifyMetadataContext } from '../../../context/ModifyMetadataContext';
import { getToken } from '../../../utils';
import { HOMEPAGE_URL } from '../../../constants';

import useUser from '../../../store/user';
import useLanguagePack from '../../../hooks/useLanguagePack';

const Header = ({ transparent = false, fixed = false, lnb }) => {
  const theme = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useContext(LayoutContext);
  const { isModified, handleModalOpen } = useContext(ModifyMetadataContext);

  const headerRef = useRef();

  const { user } = useUser(
    state => ({
      user: state.user,
    }),
    shallow
  );

  const languageKeyStrings = {
    signInButton: useLanguagePack('helpcenter711gnb_buttonlabel_1'),
    signUpButton: useLanguagePack('helpcenter711gnb_buttonlabel_2'),
    guideButton: useLanguagePack('helpcenter721_tab_4'),
  };

  useEffect(() => {
    window.addEventListener('scroll', onScrollEvt);
    return () => {
      window.removeEventListener('scroll', onScrollEvt);
    };
  }, []);

  const onScrollEvt = e => {
    const headerElem = _.isEmpty(lnb) ? document.getElementsByClassName('header-wrapper')[0] : document.getElementsByTagName('header')[0];
    // const userIconElem = document.getElementsByClassName('gnb-user-icon')[0].childNodes[0].childNodes[0];
    if (document.body.scrollTop > 25 || document.documentElement.scrollTop > 25) {
      if (_.isEmpty(lnb)) headerElem.style.background = theme.palette.darkgray;
      headerElem.style.boxShadow = '0 2px 10px 0 rgba(0, 0, 0, 0.25)';
      // userIconElem?.style.fill = '#fff';
    } else {
      if (_.isEmpty(lnb))
        headerElem.style.background = 'linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%), transparent';
      headerElem.style.boxShadow = 'none';
      // userIconElem?.style.fill = '#999';
    }
  };

  const onClickEvt = cb => {
    if (isModified) handleModalOpen(cb);
    else cb();
  };

  const renderMenuBar = useCallback(() => {
    if (_.isEmpty(user) || /\/(changepw|terms)/.test(location.pathname)) {
      if (/\/(help|notice|faq)/.test(location.pathname)) {
        return (
          <div className='gnb-sign-button'>
            <NewButton onClick={() => navigate('/signin')} variant={'outlined'} color={'secondary'}>
              {languageKeyStrings.signInButton}
            </NewButton>
            <NewButton onClick={() => navigate('/signup')}>{languageKeyStrings.signUpButton}</NewButton>
          </div>
        );
      } else {
        return (
          <div className='gnb-select-language'>
            <SelectLanguage />
          </div>
        );
      }
    } else {
      return (
        <>
          <Menu />
          <UserInfo />
        </>
      );
    }
  }, [user, location.path]);

  return (
    <StyledHeader transparent={transparent} fixed={fixed} isMobile={isMobile} ref={headerRef}>
      <div className='header-wrapper'>
        <LogoDiv isMobile={isMobile}>
          <img
            src='/assets/image/logo/keycut.svg'
            onClick={() => window.open(HOMEPAGE_URL, '_blank')}
            tabIndex={0}
            role={'banner'}
            alt={process.env.REACT_APP_LEGACY_NAME}
          />
          <Divider />
          <img
            src='/assets/image/logo/artist_space.svg'
            onClick={() => onClickEvt(() => navigate(getToken() ? '/dashboard' : '/'))}
            tabIndex={0}
            role={'banner'}
            alt={process.env.REACT_APP_PROJECT_NAME}
          />
        </LogoDiv>
        <NavLink to='/guide' className='text-white ml-auto guide_link'>
          <img className='guide_info' alt={'guide_info'} src='/assets/image/icon/guide_info.svg' />
          {languageKeyStrings.guideButton}
        </NavLink>
        {renderMenuBar()}
      </div>
      {!_.isEmpty(lnb) && lnb}
    </StyledHeader>
  );
};

export default Header;

const StyledHeader = styled.header`
  top: 0;
  /* left: 0; */
  color: #fff;
  position: ${({ fixed }) => (fixed ? 'absolute' : 'sticky')};
  /* min-width: 1200px; */
  width: 100%;
  /* height: 152px; */
  z-index: 9999;
  transition: all 0.5s ease 0s;
  .guide_link {
    width: 107px;
    height: 41px;
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 6px;
    background-color: #444;
    border-radius: 100px;
    margin-right: 20px;
  }
  .guide_info {
    width: 24px;
    height: 24px;
  }
  & > .header-wrapper {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin: 0 auto;
    height: 64px;
    ${props =>
      props.transparent
        ? css`
            background: linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%);
            box-shadow: none;
          `
        : css`
            background-color: ${({ theme }) => theme.palette.black};
            /* box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.25); */
          `};
    ${props =>
      props.isMobile
        ? css`
            width: 100%;
          `
        : css`
            /* @media (max-width: 1200px) {
              width: 1200px;
              padding: ${({ theme }) => `0 ${theme.spacing(8)}`};
            }
            @media (min-width: 1201px) { */
            padding: ${({ theme }) => `0 ${theme.spacing(8)}`};
            /* } */
          `};
  }
  & .gnb-upload-btn.focused {
    border-color: ${({ theme }) => theme.palette.channelWhite};
    background-color: ${({ theme }) => theme.palette.secondaryBlue};
  }
  & .gnb-select-language {
    ${props =>
      props.isMobile &&
      css`
        padding-right: ${({ theme }) => theme.spacing(4)};
      `};
  }
  & .gnb-sign-button {
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(4)};
  }
`;
